// src/index.ts - XCon Studio Vite Plugin with SASS Support
import { createLogger } from './utils/logger';
import { pathUtils } from './utils/path-utils';
import { processTemplateUrl } from './processors/template';
import { processStyleUrls } from './processors/style';
import { processResources } from './processors/resource';
import { createDefaultOptions, XConVitePluginOptions } from "./types";
import type { Plugin, HmrContext, ModuleNode } from 'vite';

/**
 * XCon Studio Vite Plugin with SASS/SCSS support
 */
export function xconVitePlugin(userOptions: XConVitePluginOptions = {}): Plugin {
    // Browser environment check
    if (typeof window !== 'undefined') {
        console.warn('[XCon Vite Plugin] Should only be used in Node.js environment (Vite build)');
        return {
            name: 'xcon-vite-plugin-browser-stub',
            enforce: 'pre',
            apply: 'build'
        } as Plugin;
    }

    const options = createDefaultOptions(userOptions);
    const logger = createLogger(options.logger);

    // Track processed files and their dependencies
    const processedFiles = new Map<string, string>();
    const fileDependencies = new Map<string, Set<string>>();

    // Track HTML/CSS/SCSS files referenced in decorators to prevent import resolution
    const decoratorFiles = new Set<string>();

    // Check dependencies
    let terser: any = null;
    let sass: any = null;

    if (options.useTerser) {
        try {
            terser = require('terser');
            logger.debug('Terser minification enabled');
        } catch (e) {
            logger.warn('Terser not found, using basic minification');
            options.useTerser = false;
        }
    }

    // Check for SASS compiler
    try {
        sass = require('sass');
        logger.debug('SASS compiler available');
    } catch (e) {
        logger.debug('SASS compiler not available - SCSS/SASS files will be processed as CSS');
    }

    return {
        name: 'xcon-vite-plugin',
        enforce: 'pre',

        buildStart() {
            logger.info('🚀 XCon Vite Plugin started');
            if (sass) {
                logger.info('✅ SASS compiler enabled');
            } else {
                logger.warn('⚠️ SASS compiler not available - install with: npm install sass --save-dev');
            }
            processedFiles.clear();
            fileDependencies.clear();
            decoratorFiles.clear();
        },

        resolveId(source: string, importer: string | undefined) {
            if (!importer) {
                return null;
            }

            const isTemplate = options.templateExtensions.some(ext => source.endsWith(ext));
            const isStyle = options.styleExtensions.some(ext => source.endsWith(ext));

            if (isTemplate || isStyle) {
                if (shouldProcessFile(importer, options)) {
                    try {
                        const fs = require('fs');
                        if (fs.existsSync(importer)) {
                            const code = fs.readFileSync(importer, 'utf8');

                            const hasDecorators = /templateUrl:\s*['"]/i.test(code) ||
                                /styleUrls:\s*\[/i.test(code);

                            if (hasDecorators) {
                                const path = require('path');
                                const resolved = path.resolve(path.dirname(importer), source);

                                decoratorFiles.add(resolved);

                                logger.debug(`🚫 Blocking import resolution: ${source} (will be inlined)`);

                                return false;
                            }
                        }
                    } catch (e) {
                        logger.debug(`Could not check decorator status for ${importer}`);
                    }
                }
            }

            return null;
        },

        load(id: string) {
            if (!shouldProcessFile(id, options)) {
                return null;
            }

            if (!id.endsWith('.ts') && !id.endsWith('.js')) {
                return null;
            }

            const fs = require('fs');
            if (!fs.existsSync(id)) {
                return null;
            }

            const originalCode = fs.readFileSync(id, 'utf8');

            const hasTemplateUrl = /templateUrl:\s*['"]/i.test(originalCode);
            const hasStyleUrls = /styleUrls:\s*\[/i.test(originalCode);

            if (!hasTemplateUrl && !hasStyleUrls) {
                return null;
            }

            logger.info(`📄 Processing: ${pathUtils.basename(id)}`);

            try {
                const result = performTransformation(
                    originalCode,
                    id,
                    options,
                    this.addWatchFile.bind(this),
                    logger,
                    terser,
                    fileDependencies
                );

                if (result.changed) {
                    processedFiles.set(id, result.code);
                    logger.info(`✅ Successfully processed: ${pathUtils.basename(id)}`);

                    return {
                        code: result.code,
                        map: result.map
                    };
                }
            } catch (error) {
                logger.error(`Processing error for ${id}:`, error);
            }

            return null;
        },

        transform(code: string, id: string) {
            if (!shouldProcessFile(id, options)) {
                return null;
            }

            if (processedFiles.has(id)) {
                return null;
            }

            const hasTemplateUrl = /templateUrl:\s*['"]/i.test(code);
            const hasStyleUrls = /styleUrls:\s*\[/i.test(code);
            const hasResources = /resources:\s*\[/i.test(code);

            if (!hasTemplateUrl && !hasStyleUrls && !hasResources) {
                return null;
            }

            logger.info(`🔧 Transform processing: ${pathUtils.basename(id)}`);

            try {
                const result = performTransformation(
                    code,
                    id,
                    options,
                    this.addWatchFile.bind(this),
                    logger,
                    terser,
                    fileDependencies
                );

                if (result.changed) {
                    processedFiles.set(id, result.code);

                    return {
                        code: result.code,
                        map: result.map
                    };
                }
            } catch (error) {
                logger.error(`Transform error for ${id}:`, error);
            }

            return null;
        },

        handleHotUpdate(ctx: HmrContext): void | ModuleNode[] | Promise<void | ModuleNode[]> {
            const { file, server } = ctx;

            const isTemplate = options.templateExtensions.some(ext => file.endsWith(ext));
            const isStyle = options.styleExtensions.some(ext => file.endsWith(ext));

            if (!isTemplate && !isStyle) {
                return;
            }

            logger.info(`🔥 Hot reload: ${pathUtils.basename(file)}`);

            const affectedModules: ModuleNode[] = [];
            const moduleGraph = server.moduleGraph;

            for (const [widgetFile, dependencies] of fileDependencies.entries()) {
                if (dependencies.has(file)) {
                    const module = moduleGraph.getModuleById(widgetFile);

                    if (module) {
                        moduleGraph.invalidateModule(module);
                        affectedModules.push(module);
                        processedFiles.delete(widgetFile);

                        logger.info(`  🔄 Reloading: ${pathUtils.basename(widgetFile)}`);
                    }
                }
            }

            if (affectedModules.length > 0) {
                logger.info(`✅ Updated ${affectedModules.length} widget(s)`);
                return affectedModules;
            }

            return;
        },

        buildEnd() {
            const count = processedFiles.size;
            logger.info(`✅ XCon Vite Plugin finished - Processed ${count} file${count !== 1 ? 's' : ''}`);

            processedFiles.clear();
            fileDependencies.clear();
            decoratorFiles.clear();
        }
    };
}

/**
 * Perform the actual transformation
 */
function performTransformation(
    code: string,
    id: string,
    options: Required<XConVitePluginOptions>,
    addWatchFile: (file: string) => void,
    logger: any,
    terser: any,
    fileDependencies: Map<string, Set<string>>
): { code: string; changed: boolean; map?: any } {
    let transformedCode = code;
    let hasChanges = false;
    let combinedSourceMap: any = null;

    const trackingAddWatchFile = (file: string) => {
        addWatchFile(file);

        if (!fileDependencies.has(id)) {
            fileDependencies.set(id, new Set());
        }
        fileDependencies.get(id)!.add(file);
    };

    // Process templateUrl
    if (/templateUrl:\s*['"]/i.test(transformedCode)) {
        const templateResult = processTemplateUrl(
            transformedCode,
            id,
            options,
            trackingAddWatchFile,
            logger
        );

        if (templateResult.changed) {
            transformedCode = templateResult.code;
            combinedSourceMap = templateResult.map;
            hasChanges = true;

            logger.debug(`  ✓ Template processed`);
        }
    }

    // Process styleUrls
    if (/styleUrls:\s*\[/i.test(transformedCode)) {
        const styleResult = processStyleUrls(
            transformedCode,
            id,
            options,
            trackingAddWatchFile,
            logger,
            terser
        );

        if (styleResult.changed) {
            transformedCode = styleResult.code;
            hasChanges = true;

            logger.debug(`  ✓ Styles processed`);
        }
    }

    // Process resources
    if (/resources:\s*\[/i.test(transformedCode)) {
        const resourceResult = processResources(
            transformedCode,
            id,
            options,
            trackingAddWatchFile,
            logger
        );

        if (resourceResult.changed) {
            transformedCode = resourceResult.code;
            hasChanges = true;
        }
    }

    return {
        code: transformedCode,
        changed: hasChanges,
        map: combinedSourceMap
    };
}

/**
 * Check if file should be processed
 */
function shouldProcessFile(id: string, options: Required<XConVitePluginOptions>): boolean {
    if (id.startsWith('\0') || id.startsWith('virtual:') || id.includes('node_modules')) {
        return false;
    }

    const hasValidExtension = options.fileExtensions.some(ext => id.endsWith(ext));

    if (!hasValidExtension) {
        return false;
    }

    if (options.excludeFiles.length > 0) {
        const fileName = pathUtils.basename(id);
        if (options.excludeFiles.some(exclude => fileName.includes(exclude))) {
            return false;
        }
    }

    if (options.includeFiles.length > 0) {
        const fileName = pathUtils.basename(id);
        return options.includeFiles.some(include => fileName.includes(include));
    }

    return true;
}

export default xconVitePlugin;